import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { X, Send, Zap, Pin, MessageCircle, CornerDownRight, Coins, Sparkles, Image as ImageIcon, Mic, Play, Pause, Trash2, Loader2, Pencil, Check } from "lucide-react";
import { api, picUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import MentionInput, { renderMentionText } from "./MentionInput";
import GiftQueue, { useGiftQueue } from "./GiftQueue";
import { TIERS, tierForCoins } from "./GiftAnimation";
import { BadgeWithCelebration } from "@/components/CreatorBadge";
import { useTopBadges } from "@/lib/badges";
import { computeWaveformFromBlob, WAVEFORM_BARS } from "@/lib/waveform";

// Renders the commenter's top-3 leaderboard badge (if any) inline with
// their name. `mode="static"` = no auto-fade because comments scroll and
// re-render constantly.
function CommentTopBadge({ userId, username }) {
  const badge = useTopBadges(userId);
  if (!badge) return null;
  return (
    <BadgeWithCelebration
      category={badge.category}
      rank={badge.rank}
      username={username}
      size="sm"
      mode="static"
    />
  );
}

// Wraps a comment avatar in a Link to /u/{username} so tapping the
// picture takes viewers straight to the author's profile. Falls back
// to a plain <div> for guest / deleted authors that have no username.
function LinkedAvatar({ username, children }) {
  if (!username) return <>{children}</>;
  return (
    <Link
      to={`/u/${username}`}
      data-testid={`comment-avatar-link-${username}`}
      className="flex-shrink-0 hover:opacity-80 transition-opacity"
      onClick={(e) => e.stopPropagation()}
    >
      {children}
    </Link>
  );
}

// Inline attachment (image or voice note) rendered under gift-comment
// text. Only appears when the parent comment is a paid gift (backend
// enforces `coins_amount > 0` on write, so any comment reaching us with
// an `attachment` field has already been validated).
function CommentAttachment({ attachment }) {
  const [expanded, setExpanded] = useState(false);
  if (!attachment) return null;
  const url = process.env.REACT_APP_BACKEND_URL
    ? `${process.env.REACT_APP_BACKEND_URL}${attachment.media_url}`
    : attachment.media_url;
  if (attachment.media_kind === "image") {
    return (
      <>
        <button
          type="button"
          onClick={() => setExpanded(true)}
          data-testid="comment-attachment-image"
          className="mt-2 block max-w-[220px] border border-[#FF007F]/40 hover:border-[#FF007F] transition-colors"
        >
          <img src={url} alt="attachment" className="w-full h-auto object-cover" />
        </button>
        {expanded && (
          <div
            className="fixed inset-0 z-[70] bg-black/90 flex items-center justify-center p-4"
            onClick={() => setExpanded(false)}
          >
            <img src={url} alt="attachment" className="max-h-[90vh] max-w-full" />
          </div>
        )}
      </>
    );
  }
  if (attachment.media_kind === "audio") {
    return (
      <VoiceNotePlayer
        url={url}
        duration={attachment.duration_sec}
        waveform={attachment.waveform}
      />
    );
  }
  return null;
}

// Bespoke voice-note player: play/pause + duration pill + a 40-bar
// waveform strip that fills in cyan as the note plays. Waveform is
// pre-computed client-side (see `lib/waveform.js`); when the array is
// missing we render a flat placeholder row so the layout doesn't jump.
function VoiceNotePlayer({ url, duration, waveform }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [pos, setPos] = useState(0);
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onTime = () => setPos(a.currentTime || 0);
    const onEnd = () => { setPlaying(false); setPos(0); };
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("ended", onEnd);
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("ended", onEnd);
    };
  }, []);
  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) { a.play(); setPlaying(true); }
    else { a.pause(); setPlaying(false); }
  };
  const total = Number(duration || 0) || 0;
  const shown = total > 0 ? Math.max(0, total - pos) : pos;
  const mm = Math.floor(shown / 60).toString().padStart(1, "0");
  const ss = Math.floor(shown % 60).toString().padStart(2, "0");
  const bars = Array.isArray(waveform) && waveform.length > 0
    ? waveform
    : new Array(40).fill(0.15);   // flat placeholder if server omitted it
  const progress = total > 0 ? Math.max(0, Math.min(1, pos / total)) : 0;

  // Tap-to-scrub: compute the fraction from the click's x position
  // inside the bar strip and seek the audio there. Works with a mouse
  // click OR a touch tap because the surrounding button uses onClick
  // (mobile Safari dispatches synthetic clicks after touchend). If
  // duration is unknown (server missed it) we skip scrubbing rather
  // than jumping to an unpredictable spot.
  const scrubTo = (e) => {
    const a = audioRef.current;
    if (!a || !total) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX ?? e.touches?.[0]?.clientX ?? 0) - rect.left;
    const frac = Math.max(0, Math.min(1, x / Math.max(1, rect.width)));
    a.currentTime = frac * total;
    setPos(a.currentTime);
    if (a.paused) { a.play(); setPlaying(true); }
  };

  return (
    <div
      data-testid="comment-attachment-audio"
      className="mt-2 inline-flex items-center gap-2 pl-1 pr-3 py-1 border border-[#00FFFF]/40 bg-[#00FFFF]/5"
    >
      <button
        type="button"
        onClick={toggle}
        aria-label={playing ? "Pause" : "Play"}
        className="h-7 w-7 flex items-center justify-center bg-[#00FFFF]/20 hover:bg-[#00FFFF]/30 text-[#00FFFF] flex-shrink-0"
      >
        {playing ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
      </button>
      {/* Waveform strip — 40 vertical bars scaled to peak amplitude.
          Bars up to the current playback position render solid cyan;
          the rest fade to a dim outline. Tap anywhere on the strip to
          scrub to that point (see `scrubTo`). */}
      <button
        type="button"
        onClick={scrubTo}
        aria-label="Scrub to position"
        data-testid="voice-note-scrub"
        className="flex items-end gap-[2px] h-6 cursor-pointer"
        style={{ width: 120 }}
      >
        {bars.slice(0, 40).map((v, i) => {
          const played = (i + 0.5) / bars.length <= progress;
          const height = Math.max(2, Math.round((Number(v) || 0) * 22));
          return (
            <span
              key={i}
              className="inline-block w-[2px] rounded-sm pointer-events-none"
              style={{
                height,
                background: played ? "#00FFFF" : "rgba(0,255,255,0.28)",
                transition: "background 120ms",
              }}
            />
          );
        })}
      </button>
      <span className="font-mono text-[11px] text-[#00FFFF] tabular-nums">
        {mm}:{ss}
      </span>
      <audio ref={audioRef} src={url} preload="none" />
    </div>
  );
}

// Coin presets that map 1:1 to the tier system. "Custom" lets viewers type any amount.
const COIN_PRESETS = [10, 50, 100, 200, 300, 500, 1000];
const DEFAULT_CREATOR_PCT = 60;

export default function CommentDrawer({ open, onClose, video, onCommentAdded }) {
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [posting, setPosting] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [replyPosting, setReplyPosting] = useState(false);
  const [coinAmount, setCoinAmount] = useState(0);          // coins attached to next comment
  const [showPicker, setShowPicker] = useState(false);
  const [customAmount, setCustomAmount] = useState("");
  const [creatorPct, setCreatorPct] = useState(DEFAULT_CREATOR_PCT);
  // Gift-only attachment for the next comment. Populated by the image
  // picker or voice-recorder; cleared once the comment is sent or the
  // user backs out of the gift picker. Backend enforces coins > 0 before
  // persisting so a rogue client can't attach media to a free comment.
  const [attachment, setAttachment] = useState(null);     // {media_id, media_kind, media_url, duration_sec}
  const [attachmentBusy, setAttachmentBusy] = useState(false);
  const [recording, setRecording] = useState(false);
  const mediaRecorderRef = useRef(null);
  const recordChunksRef = useRef([]);
  const recordStartRef = useRef(0);
  const fileInputRef = useRef(null);
  const { user } = useAuth();
  const isVideoOwner = user && video?.user_id && user.user_id === video.user_id;

  // Animation queue — recent gifts play on open, then any new gift the viewer sees.
  const { queue, pushAll, push, clear } = useGiftQueue();

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    // Fetch comments + queue recent gifts in parallel.
    Promise.all([
      api.get(`/videos/${video.id}/comments`).catch(() => ({ data: [] })),
      api.get(`/videos/${video.id}/recent-gifts?limit=6`).catch(() => ({ data: { gifts: [] } })),
      api.get(`/settings/gifts`).catch(() => ({ data: null })),
    ])
      .then(([cr, gr, sr]) => {
        setComments(cr.data || []);
        const gifts = (gr.data?.gifts || []).reverse(); // oldest → newest so latest lands last
        if (gifts.length) pushAll(gifts);
        if (sr.data?.creator_pct) setCreatorPct(sr.data.creator_pct);
      })
      .finally(() => setLoading(false));
    // Reset picker each open so a stale coin count never sneaks into the next comment.
    setCoinAmount(0);
    setCustomAmount("");
    setShowPicker(false);
    setAttachment(null);
    setRecording(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, video?.id]);

  useEffect(() => {
    // When the drawer closes, drop pending animations so they don't replay later.
    if (!open) clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const { parents, repliesByParent } = useMemo(() => {
    const p = [];
    const r = {};
    for (const c of comments) {
      if (c.parent_comment_id) {
        (r[c.parent_comment_id] ||= []).push(c);
      } else {
        p.push(c);
      }
    }
    Object.keys(r).forEach((k) => {
      r[k].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    });
    return { parents: p, repliesByParent: r };
  }, [comments]);

  // Gift-only image attach: fires the file picker, uploads to
  // /comments/media, and stages the resulting `{media_id, media_kind,
  // media_url}` so the next `submit()` bundles it onto the gift comment.
  const pickImage = () => {
    if (attachmentBusy || recording) return;
    fileInputRef.current?.click();
  };
  const onImagePicked = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";  // let user re-pick the same file if they cancel later
    if (!file) return;
    setAttachmentBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const r = await api.post("/comments/media", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setAttachment(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Image upload failed");
    } finally {
      setAttachmentBusy(false);
    }
  };

  // Gift-only voice-note record: MediaRecorder → blob → upload. We use
  // whatever mime type the browser picks (Chrome=webm/opus, Safari=mp4/
  // aac) and pass duration_sec so the recipient's bubble shows the
  // length without re-decoding the audio.
  const startRecord = async () => {
    if (attachmentBusy || attachment) return;
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error("Recording not supported on this device");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      recordChunksRef.current = [];
      recordStartRef.current = Date.now();
      rec.addEventListener("dataavailable", (ev) => {
        if (ev.data && ev.data.size) recordChunksRef.current.push(ev.data);
      });
      rec.addEventListener("stop", async () => {
        stream.getTracks().forEach((t) => t.stop());
        const dur = (Date.now() - recordStartRef.current) / 1000;
        if (dur < 0.5) { setRecording(false); return; }  // ignore taps
        if (dur > 60) { toast.error("Voice note too long (max 60s)"); setRecording(false); return; }
        const blob = new Blob(recordChunksRef.current, { type: rec.mimeType || "audio/webm" });
        const ext = (blob.type.split("/")[1] || "webm").split(";")[0];
        // Peak-normalized 40-bar waveform pre-computed client-side so
        // recipients see the audio shape instantly — no re-decode.
        const bars = await computeWaveformFromBlob(blob);
        setAttachmentBusy(true);
        try {
          const form = new FormData();
          form.append("file", blob, `voice.${ext}`);
          form.append("duration_sec", String(dur.toFixed(2)));
          if (bars.length) form.append("waveform", JSON.stringify(bars));
          const r = await api.post("/comments/media", form, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          setAttachment(r.data);
        } catch (err) {
          toast.error(err?.response?.data?.detail || "Voice upload failed");
        } finally {
          setAttachmentBusy(false);
          setRecording(false);
        }
      });
      mediaRecorderRef.current = rec;
      rec.start();
      setRecording(true);
    } catch {
      toast.error("Microphone access denied");
    }
  };
  const stopRecord = () => {
    const rec = mediaRecorderRef.current;
    if (rec && rec.state !== "inactive") rec.stop();
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error("Sign in to comment");
      return;
    }
    // Attachments require a paid gift — the button only appears when the
    // coin picker is open, but we guard defensively.
    if (attachment && coinAmount <= 0) {
      toast.error("Attachments require a coin gift");
      return;
    }
    // A gift alone counts as a valid submission (empty text OK if coins > 0).
    if (!text.trim() && coinAmount <= 0 && !attachment) return;
    setPosting(true);
    try {
      const r = await api.post(`/videos/${video.id}/comments`, {
        text,
        coins_amount: coinAmount || undefined,
        media_id: attachment?.media_id,
        media_kind: attachment?.media_kind,
      });
      setComments([r.data, ...comments]);
      setText("");
      // If we attached coins, enqueue the tier animation immediately for the sender.
      if (coinAmount > 0 && r.data?.coins_amount) {
        push({
          id: r.data.id,
          coins_amount: r.data.coins_amount,
          gift_tier: r.data.gift_tier || tierForCoins(r.data.coins_amount),
          text: r.data.text,
          tipper: r.data.author,
        });
      }
      setCoinAmount(0);
      setCustomAmount("");
      setShowPicker(false);
      setAttachment(null);
      onCommentAdded?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to post comment");
    } finally {
      setPosting(false);
    }
  };

  const submitReply = async (parent_comment_id) => {
    if (!user) {
      toast.error("Sign in to reply");
      return;
    }
    if (!replyText.trim()) return;
    setReplyPosting(true);
    try {
      const r = await api.post(`/videos/${video.id}/comments`, {
        text: replyText,
        parent_comment_id,
      });
      setComments((prev) => [
        r.data,
        ...prev.map((c) =>
          c.id === parent_comment_id
            ? { ...c, reply_count: (c.reply_count || 0) + 1 }
            : c
        ),
      ]);
      setReplyText("");
      setReplyingTo(null);
      onCommentAdded?.();
    } catch {
      toast.error("Failed to post reply");
    } finally {
      setReplyPosting(false);
    }
  };

  const togglePin = async (comment_id) => {
    try {
      const r = await api.post(`/comments/${comment_id}/pin`);
      setComments((prev) => {
        const next = prev.map((c) =>
          c.id === comment_id ? { ...c, pinned: r.data.pinned } : c
        );
        return [...next].sort((a, b) => {
          const p = (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0);
          if (p !== 0) return p;
          return new Date(b.created_at) - new Date(a.created_at);
        });
      });
      toast.success(r.data.pinned ? "Comment pinned" : "Comment unpinned");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not pin");
    }
  };

  // Delete a comment (author / video creator / admin) — cascades to
  // replies if it's a top-level comment. Optimistically drops the row
  // from state; refetch happens on the next open so we don't have to
  // reconcile counts manually here.
  const deleteComment = async (comment_id) => {
    const confirmed = window.confirm("Delete this comment? This can't be undone.");
    if (!confirmed) return;
    try {
      await api.delete(`/comments/${comment_id}`);
      setComments((prev) =>
        prev.filter((c) => c.id !== comment_id && c.parent_comment_id !== comment_id)
      );
      toast.success("Comment deleted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete comment");
    }
  };

  // Inline-edit state. Only one comment can be in edit mode at a time.
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const startEdit = (c) => {
    setEditingId(c.id);
    setEditText(c.text || "");
  };
  const cancelEdit = () => {
    setEditingId(null);
    setEditText("");
  };
  const saveEdit = async (comment_id) => {
    const text = editText.trim();
    if (!text) {
      toast.error("Comment can't be empty");
      return;
    }
    try {
      const r = await api.patch(`/comments/${comment_id}`, { text });
      setComments((prev) =>
        prev.map((c) => (c.id === comment_id
          ? { ...c, text: r.data.text, edited_at: r.data.edited_at, edited_by_admin: r.data.edited_by_admin }
          : c
        ))
      );
      cancelEdit();
      toast.success("Comment updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save edit");
    }
  };

  const renderCommentRow = (c, { isReply = false } = {}) => {
    const isBoost = c.kind === "boost";
    const pinned = !!c.pinned;
    const canPin = isVideoOwner && !isReply; // only top-level pinnable
    const PinBtn = canPin ? (
      <button
        data-testid={`pin-btn-${c.id}`}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          togglePin(c.id);
        }}
        aria-label={pinned ? "Unpin comment" : "Pin comment"}
        title={pinned ? "Unpin comment" : "Pin comment"}
        className={`h-6 w-6 flex items-center justify-center transition-colors flex-shrink-0 ${
          pinned ? "text-[#39FF14]" : "text-white/25 hover:text-white/70"
        }`}
      >
        <Pin
          className="h-3.5 w-3.5"
          fill={pinned ? "#39FF14" : "none"}
          strokeWidth={1.6}
        />
      </button>
    ) : null;
    const PinnedBadge = pinned && !isReply ? (
      <div
        className="flex items-center gap-1 mb-1.5"
        data-testid={`pinned-badge-${c.id}`}
      >
        <Pin className="h-3 w-3 text-[#39FF14]" fill="#39FF14" strokeWidth={0} />
        <span className="font-mono text-[9px] uppercase tracking-widest text-[#39FF14]">
          pinned by creator
        </span>
      </div>
    ) : null;

    // Permissions
    const authorId = c.user_id || c.author?.user_id;
    const isAuthor = !!(user && authorId && user.user_id === authorId);
    const isAdmin = !!(user && user.is_admin);
    const canDelete = isAuthor || isVideoOwner || isAdmin;
    const canEdit = isAuthor || isAdmin;
    const isEditing = editingId === c.id;
    const EditDeleteBtns = (
      <div className="flex items-center gap-0.5 flex-shrink-0">
        {canEdit && !isBoost && (
          <button
            data-testid={`edit-btn-${c.id}`}
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); startEdit(c); }}
            aria-label="Edit comment"
            title="Edit comment"
            className="h-6 w-6 flex items-center justify-center text-white/25 hover:text-[#00FFFF] transition-colors"
          >
            <Pencil className="h-3 w-3" strokeWidth={1.8} />
          </button>
        )}
        {canDelete && (
          <button
            data-testid={`delete-btn-${c.id}`}
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); deleteComment(c.id); }}
            aria-label="Delete comment"
            title="Delete comment"
            className="h-6 w-6 flex items-center justify-center text-white/25 hover:text-[#FF007F] transition-colors"
          >
            <Trash2 className="h-3 w-3" strokeWidth={1.8} />
          </button>
        )}
      </div>
    );

    if (isBoost) {
      const amt = ((c.amount_cents || 0) / 100).toFixed(2);
      return (
        <div
          data-testid={`boost-comment-${c.id}`}
          className={`p-3 border bg-gradient-to-r from-[#FF007F]/10 to-transparent transition-all ${
            pinned
              ? "border-[#39FF14]/70 shadow-[0_0_22px_rgba(57,255,20,0.35)]"
              : "border-[#FF007F]/50 shadow-[0_0_18px_rgba(255,0,127,0.2)]"
          }`}
        >
          {PinnedBadge}
          <div className="flex items-center gap-2 mb-2">
            <Zap className="h-3.5 w-3.5 text-[#FF007F]" fill="#FF007F" strokeWidth={0} />
            <span className="font-mono text-[10px] uppercase tracking-widest text-[#FF007F]">
              boost
            </span>
            <span className="font-display font-black text-[#FF007F] text-sm">
              +${amt}
            </span>
            <span className="font-mono text-[10px] text-white/30 ml-auto">
              {new Date(c.created_at).toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </span>
            {PinBtn}
            {EditDeleteBtns}
          </div>
          <div className="flex gap-2 items-start">
            <LinkedAvatar username={c.author?.username}>
              {c.author?.picture ? (
                <img
                  src={picUrl(c.author.picture)}
                  alt=""
                  className="h-7 w-7 object-cover border border-[#FF007F]/40 flex-shrink-0"
                />
              ) : (
                <div className="h-7 w-7 bg-[#FF007F]/20 border border-[#FF007F]/40 flex items-center justify-center text-[#FF007F] text-xs font-bold flex-shrink-0">
                  {c.author?.name?.[0]?.toUpperCase() || "?"}
                </div>
              )}
            </LinkedAvatar>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold text-white truncate">
                {c.author?.name || "Anonymous"}
                {!c.author?.user_id && (
                  <span className="ml-2 font-mono text-[9px] uppercase tracking-widest text-white/30">
                    guest
                  </span>
                )}
              </p>
              {c.text ? (
                <p className="text-sm text-white/90 mt-0.5 italic break-words">
                  “{renderMentionText(c.text)}”
                </p>
              ) : (
                <p className="text-xs text-white/40 mt-0.5 italic">
                  sent a silent boost
                </p>
              )}
            </div>
          </div>
        </div>
      );
    }

    // --- Gift-attached user comments (coins > 0) --------------------------
    if (c.coins_amount && c.coins_amount > 0) {
      const tier = c.gift_tier || tierForCoins(c.coins_amount) || 10;
      const cfg = TIERS[tier];
      const replay = () => {
        push({
          id: c.id,
          coins_amount: c.coins_amount,
          gift_tier: tier,
          text: c.text,
          tipper: c.author,
        });
      };
      return (
        <div
          data-testid={`gift-comment-${c.id}`}
          data-tier={tier}
          className={`p-3 border transition-all ${
            pinned
              ? "border-[#39FF14]/70 shadow-[0_0_22px_rgba(57,255,20,0.35)]"
              : ""
          }`}
          style={{
            borderColor: pinned ? undefined : `${cfg.color}55`,
            background: `linear-gradient(90deg, ${cfg.color}18 0%, transparent 100%)`,
            boxShadow: pinned ? undefined : `0 0 18px ${cfg.color}20`,
          }}
        >
          {PinnedBadge}
          <div className="flex items-center gap-2 mb-2">
            <Sparkles
              className="h-3.5 w-3.5"
              style={{ color: cfg.color }}
              strokeWidth={2}
            />
            <span
              className="font-mono text-[10px] uppercase tracking-widest"
              style={{ color: cfg.color }}
            >
              // {cfg.label}
            </span>
            <span
              className="font-display font-black text-sm"
              style={{ color: cfg.color }}
            >
              +{c.coins_amount.toLocaleString()} coins
            </span>
            <button
              onClick={replay}
              data-testid={`gift-replay-${c.id}`}
              title="Replay animation"
              className="ml-auto font-mono text-[9px] uppercase tracking-widest text-white/40 hover:text-white/80 border border-white/10 hover:border-white/30 px-2 py-0.5 transition-colors"
            >
              ▶ replay
            </button>
            {PinBtn}
            {EditDeleteBtns}
          </div>
          <div className="flex gap-2 items-start">
            <LinkedAvatar username={c.author?.username}>
              {c.author?.picture ? (
                <img
                  src={picUrl(c.author.picture)}
                  alt=""
                  className="h-7 w-7 object-cover flex-shrink-0 border"
                  style={{ borderColor: cfg.color + "88" }}
                />
              ) : (
                <div
                  className="h-7 w-7 flex items-center justify-center text-xs font-bold flex-shrink-0 border"
                  style={{
                    color: cfg.color,
                    borderColor: cfg.color + "88",
                    background: cfg.color + "22",
                  }}
                >
                  {c.author?.name?.[0]?.toUpperCase() || "?"}
                </div>
              )}
            </LinkedAvatar>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold text-white truncate flex items-center gap-1.5">
                <span className="truncate">{c.author?.name || "Anonymous"}</span>{" "}
                <span className="font-mono text-white/30">
                  @{c.author?.username}
                </span>
                <CommentTopBadge userId={c.author?.user_id} username={c.author?.username} />
              </p>
              {c.text && (
                <p className="text-sm text-white/90 mt-0.5 break-words">
                  {renderMentionText(c.text)}
                </p>
              )}
              <CommentAttachment attachment={c.attachment} />
            </div>
          </div>
        </div>
      );
    }

    return (
      <div
        data-testid={`comment-${c.id}`}
        className={`flex gap-3 ${
          pinned && !isReply
            ? "p-3 border border-[#39FF14]/40 bg-[#39FF14]/5 shadow-[0_0_14px_rgba(57,255,20,0.15)]"
            : ""
        }`}
      >
        {isReply && (
          <CornerDownRight
            className="h-4 w-4 text-white/25 mt-1 flex-shrink-0"
            strokeWidth={1.6}
          />
        )}
        <LinkedAvatar username={c.author?.username}>
          {c.author?.picture ? (
            <img
              src={picUrl(c.author.picture)}
              alt=""
              className={`${
                isReply ? "h-6 w-6" : "h-8 w-8"
              } object-cover border border-white/10 flex-shrink-0`}
            />
          ) : (
            <div
              className={`${
                isReply ? "h-6 w-6 text-[10px]" : "h-8 w-8 text-xs"
              } bg-[#00FFFF]/20 flex items-center justify-center text-[#00FFFF] font-bold flex-shrink-0`}
            >
              {c.author?.name?.[0]?.toUpperCase() || "?"}
            </div>
          )}
        </LinkedAvatar>
        <div className="min-w-0 flex-1">
          {PinnedBadge}
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs text-white/70 mb-1 truncate flex items-center gap-1.5">
              <span className="font-semibold text-white">
                @{c.author?.username}
              </span>
              <CommentTopBadge userId={c.author?.user_id} username={c.author?.username} />
              <span className="font-mono text-white/30">
                · {new Date(c.created_at).toLocaleString()}
              </span>
              {c.edited_at && (
                <span className="font-mono text-white/30 italic">
                  · edited{c.edited_by_admin ? " by admin" : ""}
                </span>
              )}
            </p>
            <div className="flex items-center gap-0.5">
              {PinBtn}
              {EditDeleteBtns}
            </div>
          </div>
          {isEditing ? (
            <div className="mt-1 flex flex-col gap-1.5" data-testid={`edit-form-${c.id}`}>
              <textarea
                data-testid={`edit-input-${c.id}`}
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                autoFocus
                rows={2}
                className="w-full bg-black border border-[#00FFFF]/40 focus:border-[#00FFFF] px-2 py-1.5 text-sm text-white outline-none resize-y"
              />
              <div className="flex items-center gap-2">
                <button
                  data-testid={`edit-save-${c.id}`}
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); saveEdit(c.id); }}
                  className="flex items-center gap-1 bg-[#00FFFF] text-black font-mono text-[10px] uppercase tracking-widest px-3 py-1.5"
                >
                  <Check className="h-3 w-3" /> save
                </button>
                <button
                  data-testid={`edit-cancel-${c.id}`}
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); cancelEdit(); }}
                  className="font-mono text-[10px] uppercase tracking-widest text-white/50 hover:text-white px-2 py-1.5"
                >
                  cancel
                </button>
              </div>
            </div>
          ) : (
            <p className="text-sm text-white break-words">{renderMentionText(c.text)}</p>
          )}
          <CommentAttachment attachment={c.attachment} />
        </div>
      </div>
    );
  };

  if (!open) return null;

  const pickerTier = coinAmount > 0 ? tierForCoins(coinAmount) : null;
  const pickerCfg = pickerTier ? TIERS[pickerTier] : null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="comment-drawer">
      {/* Gift animation queue — sits above the backdrop but below the drawer
          panel so the semi-transparent panel floats over the fireworks. */}
      <GiftQueue queue={queue} onFinish={clear} />

      <button
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
        aria-label="Close"
        data-testid="comment-backdrop"
      />
      <div
        className={`relative w-full sm:max-w-md h-full border-l border-white/10 flex flex-col transition-colors duration-300 ${
          queue.length > 0
            ? "bg-[#0A0A0C]/55 backdrop-blur-sm"
            : "bg-[#0A0A0C]/95 backdrop-blur-md"
        }`}
      >
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#39FF14]">
              // chatter
            </p>
            <h3 className="font-display font-bold text-lg mt-1">
              {comments.length} comment{comments.length !== 1 ? "s" : ""}
            </h3>
          </div>
          <button
            onClick={onClose}
            data-testid="close-comments-btn"
            className="h-9 w-9 border border-white/10 hover:border-[#FF007F]/50 hover:text-[#FF007F] transition-colors flex items-center justify-center"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 scrollbar-hide">
          {loading ? (
            <div className="font-mono text-xs text-white/40 uppercase tracking-widest">
              loading…
            </div>
          ) : parents.length === 0 ? (
            <div className="text-center py-12 text-white/40">
              <p className="font-display text-lg">No comments yet</p>
              <p className="font-mono text-xs mt-2 uppercase tracking-wider">
                be the first to break the silence
              </p>
            </div>
          ) : (
            <ul className="space-y-5">
              {parents.map((p) => {
                const replies = repliesByParent[p.id] || [];
                const replying = replyingTo === p.id;
                return (
                  <li key={p.id} className="space-y-2">
                    {renderCommentRow(p)}
                    <div className="flex items-center gap-3 pl-11 mt-1">
                      <button
                        data-testid={`reply-toggle-${p.id}`}
                        onClick={() =>
                          setReplyingTo(replying ? null : p.id)
                        }
                        className="font-mono text-[10px] uppercase tracking-widest text-white/40 hover:text-[#00FFFF] flex items-center gap-1"
                      >
                        <MessageCircle className="h-3 w-3" strokeWidth={1.6} />
                        {replying ? "cancel" : "reply"}
                      </button>
                      {replies.length > 0 && (
                        <span
                          data-testid={`reply-count-${p.id}`}
                          className="font-mono text-[10px] uppercase tracking-widest text-white/30"
                        >
                          · {replies.length} repl{replies.length === 1 ? "y" : "ies"}
                        </span>
                      )}
                    </div>

                    {replies.length > 0 && (
                      <ul
                        data-testid={`replies-${p.id}`}
                        className="pl-11 space-y-3 border-l border-white/5 ml-4"
                      >
                        {replies.map((r) => (
                          <li key={r.id} className="pl-3">
                            {renderCommentRow(r, { isReply: true })}
                          </li>
                        ))}
                      </ul>
                    )}

                    {replying && (
                      <div className="pl-11">
                        <div className="flex gap-2 pl-3 border-l border-[#00FFFF]/30">
                          <MentionInput
                            value={replyText}
                            onChange={setReplyText}
                            onEnterKey={() => submitReply(p.id)}
                            placeholder={
                              user
                                ? `Reply to @${p.author?.username || p.author?.name || "…"}`
                                : "Sign in to reply"
                            }
                            disabled={!user || replyPosting}
                            testId={`reply-input-${p.id}`}
                            autoFocus
                            className="w-full bg-white/5 border border-white/10 text-white placeholder:text-white/30 px-3 py-2 font-mono text-xs focus:outline-none focus:border-[#00FFFF]"
                          />
                          <button
                            type="button"
                            data-testid={`reply-submit-${p.id}`}
                            onClick={() => submitReply(p.id)}
                            disabled={!user || replyPosting || !replyText.trim()}
                            className="bg-[#00FFFF] text-black px-3 py-2 disabled:opacity-30 hover:shadow-[0_0_10px_rgba(0,255,255,0.4)] transition-all"
                          >
                            <Send className="h-3 w-3" />
                          </button>
                        </div>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Coin picker (collapsible) */}
        {showPicker && (
          <div
            data-testid="coin-picker"
            className="border-t border-white/10 p-4 bg-black/60"
          >
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FF007F] mb-2">
              // attach.coins
            </p>
            <div className="flex flex-wrap gap-2 mb-3">
              {COIN_PRESETS.map((n) => {
                const cfg = TIERS[n];
                const active = coinAmount === n && !customAmount;
                return (
                  <button
                    key={n}
                    type="button"
                    data-testid={`coin-preset-${n}`}
                    onClick={() => {
                      setCoinAmount(n);
                      setCustomAmount("");
                    }}
                    className={`px-3 py-1.5 font-mono text-[11px] uppercase tracking-widest border transition-all ${
                      active
                        ? "text-black font-bold"
                        : "text-white/70 hover:text-white"
                    }`}
                    style={{
                      background: active ? cfg.color : "transparent",
                      borderColor: active ? cfg.color : cfg.color + "55",
                    }}
                    title={`${cfg.label} · $${(n * 0.01).toFixed(2)} sent · $${((n * creatorPct / 100) * 0.01).toFixed(2)} to creator`}
                  >
                    {n}
                  </button>
                );
              })}
            </div>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min="1"
                max="10000"
                inputMode="numeric"
                value={customAmount}
                data-testid="coin-custom-input"
                onChange={(e) => {
                  const v = e.target.value.replace(/[^0-9]/g, "");
                  setCustomAmount(v);
                  const n = parseInt(v || "0", 10);
                  setCoinAmount(n > 0 ? n : 0);
                }}
                placeholder="custom amount"
                className="flex-1 bg-white/5 border border-white/10 focus:border-[#FF007F] focus:ring-1 focus:ring-[#FF007F]/40 outline-none px-3 py-2 font-mono text-xs text-white placeholder:text-white/30"
              />
              <button
                type="button"
                onClick={() => {
                  setCoinAmount(0);
                  setCustomAmount("");
                }}
                data-testid="coin-clear-btn"
                className="font-mono text-[10px] uppercase tracking-widest text-white/40 hover:text-white/80 px-2"
              >
                clear
              </button>
            </div>
            {coinAmount > 0 && pickerCfg && (
              <div
                data-testid="coin-preview"
                className="mt-3 flex flex-col gap-0.5 font-mono text-[10px] uppercase tracking-widest"
                style={{ color: pickerCfg.color }}
              >
                <div className="flex items-center gap-2">
                  <Sparkles className="h-3 w-3" />
                  {coinAmount.toLocaleString()} coins · {pickerCfg.label} tier ·
                  ${(coinAmount * 0.01).toFixed(2)}
                </div>
                <div className="text-white/50 ml-5">
                  → {Math.floor((coinAmount * creatorPct) / 100).toLocaleString()} coins
                  (${((Math.floor((coinAmount * creatorPct) / 100)) * 0.01).toFixed(2)})
                  to creator · {creatorPct}/{100 - creatorPct} split
                </div>
              </div>
            )}

            {/* Gift-only image + voice-note picker. Only usable when coins
                are staged — spec: "you can only do this if you are
                gifting coins in comments section". */}
            <div className="mt-3 flex items-center gap-2" data-testid="comment-attachment-picker">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={onImagePicked}
                className="hidden"
                data-testid="comment-image-input"
              />
              <button
                type="button"
                onClick={pickImage}
                disabled={coinAmount <= 0 || attachmentBusy || recording || !!attachment}
                data-testid="comment-attach-image-btn"
                className={`flex items-center gap-1.5 px-3 py-2 border font-mono text-[10px] uppercase tracking-widest transition-all ${
                  coinAmount <= 0
                    ? "border-white/10 text-white/25 cursor-not-allowed"
                    : "border-[#FF007F]/60 text-[#FF007F] hover:bg-[#FF007F]/10"
                }`}
                title={coinAmount <= 0 ? "Attach coins first to add a photo" : "Attach a photo"}
              >
                <ImageIcon className="h-3.5 w-3.5" />
                photo
              </button>
              <button
                type="button"
                onClick={recording ? stopRecord : startRecord}
                disabled={coinAmount <= 0 || attachmentBusy || (!recording && !!attachment)}
                data-testid="comment-attach-voice-btn"
                className={`flex items-center gap-1.5 px-3 py-2 border font-mono text-[10px] uppercase tracking-widest transition-all ${
                  recording
                    ? "border-[#FF007F] bg-[#FF007F] text-black animate-pulse"
                    : coinAmount <= 0
                    ? "border-white/10 text-white/25 cursor-not-allowed"
                    : "border-[#00FFFF]/60 text-[#00FFFF] hover:bg-[#00FFFF]/10"
                }`}
                title={coinAmount <= 0 ? "Attach coins first to record a voice note" : (recording ? "Stop recording" : "Record voice note (max 60s)")}
              >
                <Mic className="h-3.5 w-3.5" />
                {recording ? "stop" : "voice"}
              </button>
              {attachmentBusy && (
                <span className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-white/50">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> uploading
                </span>
              )}
              {attachment && !attachmentBusy && (
                <div
                  data-testid="comment-attachment-preview"
                  className="flex items-center gap-2 pl-2 pr-1 py-1 border border-[#39FF14]/50 bg-[#39FF14]/5"
                >
                  {attachment.media_kind === "image" ? (
                    <>
                      <ImageIcon className="h-3.5 w-3.5 text-[#39FF14]" />
                      <span className="font-mono text-[10px] text-[#39FF14] uppercase tracking-widest">
                        photo attached
                      </span>
                    </>
                  ) : (
                    <>
                      <Mic className="h-3.5 w-3.5 text-[#39FF14]" />
                      <span className="font-mono text-[10px] text-[#39FF14] uppercase tracking-widest">
                        {Math.round(attachment.duration_sec || 0)}s voice
                      </span>
                    </>
                  )}
                  <button
                    type="button"
                    onClick={() => setAttachment(null)}
                    className="text-white/50 hover:text-[#FF007F] transition-colors"
                    aria-label="Remove attachment"
                    data-testid="comment-attachment-remove"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        <form
          onSubmit={submit}
          className="border-t border-white/10 p-4 flex gap-2"
          data-testid="comment-form"
        >
          <button
            type="button"
            onClick={() => setShowPicker((v) => !v)}
            data-testid="coin-toggle-btn"
            title="Attach coins to your comment"
            className={`flex items-center justify-center h-[42px] w-11 border transition-all ${
              coinAmount > 0
                ? "text-black font-bold"
                : showPicker
                ? "border-[#FF007F] text-[#FF007F]"
                : "border-white/10 text-white/40 hover:text-[#FF007F] hover:border-[#FF007F]/50"
            }`}
            style={
              coinAmount > 0 && pickerCfg
                ? { background: pickerCfg.color, borderColor: pickerCfg.color }
                : undefined
            }
          >
            {coinAmount > 0 ? (
              <span className="font-mono text-[10px] leading-none">
                +{coinAmount >= 1000 ? `${(coinAmount / 1000).toFixed(coinAmount % 1000 === 0 ? 0 : 1)}K` : coinAmount}
              </span>
            ) : (
              <Coins className="h-4 w-4" strokeWidth={1.8} />
            )}
          </button>
          <MentionInput
            value={text}
            onChange={setText}
            placeholder={
              user
                ? coinAmount > 0
                  ? "Say something with your gift…"
                  : "Add a comment…"
                : "Sign in to comment"
            }
            disabled={!user || posting}
            testId="comment-input"
            className="w-full bg-white/5 border border-white/10 text-white placeholder:text-white/30 px-3 py-2.5 font-mono text-sm focus:outline-none focus:border-[#00FFFF] focus:ring-1 focus:ring-[#00FFFF]/40"
          />
          <button
            type="submit"
            disabled={!user || posting || (!text.trim() && coinAmount <= 0)}
            data-testid="comment-submit-btn"
            className="bg-[#39FF14] text-black px-4 py-2.5 disabled:opacity-30 hover:shadow-[0_0_15px_rgba(57,255,20,0.5)] transition-all"
          >
            <Send className="h-4 w-4" />
          </button>
        </form>
      </div>
    </div>
  );
}
